import { useState, useCallback, useMemo } from "react";
import { Badge } from "@/components/ui/badge";
import { ClientReferralKanbanCardItem } from "@/components/referrals/ClientReferralKanbanCardItem";
import { ClientReferralWithUI } from "@/api/clientReferrals";
import { getClientReferralConfig, ClientReferralColumnConfig } from "@/lib/kanbanConfigs.tsx";

// Client referral status type
type ClientReferralStatus = 'pending' | 'contacted' | 'meeting_scheduled' | 'converted' | 'rejected';

interface ClientReferralKanbanBoardProps {
  referrals: ClientReferralWithUI[];
  onStatusChange: (referralId: string, newStatus: ClientReferralStatus) => void;
  onViewReferral: (referral: ClientReferralWithUI) => void;
  customColumns?: ClientReferralColumnConfig[]; // Optional custom columns override
  syncingReferrals?: Set<string>; // IDs of referrals currently syncing
}

export function ClientReferralKanbanBoard({ 
  referrals, 
  onStatusChange, 
  onViewReferral, 
  customColumns, 
  syncingReferrals = new Set() 
}: ClientReferralKanbanBoardProps) {
  
  const [draggedItem, setDraggedItem] = useState<string | null>(null);
  const [dragOverColumn, setDragOverColumn] = useState<ClientReferralStatus | null>(null);
  
  // Get the status columns configuration - use custom columns if provided, otherwise use default config
  const statusColumns = useMemo(() => customColumns || getClientReferralConfig(), [customColumns]);

  const handleDragStart = useCallback((e: React.DragEvent, referralId: string) => {
    setDraggedItem(referralId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', referralId);
    
    // Create a custom drag image for better visual feedback
    const dragImage = e.currentTarget.cloneNode(true) as HTMLElement;
    dragImage.style.transform = 'rotate(5deg)';
    dragImage.style.opacity = '0.8';
    e.dataTransfer.setDragImage(dragImage, 0, 0);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, columnId: ClientReferralStatus) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    
    // Only update drag over state if it's different
    if (dragOverColumn !== columnId) {
      setDragOverColumn(columnId);
    }
  }, [dragOverColumn]);
  
  const handleDragLeave = useCallback((e: React.DragEvent) => {
    // Only clear if we're actually leaving the drop zone
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setDragOverColumn(null);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent, newStatus: ClientReferralStatus) => {
    e.preventDefault();
    setDragOverColumn(null);
    
    if (draggedItem) {
      // Find the referral being moved
      const referral = referrals.find(r => r.id === draggedItem);
      
      // Only update if status is actually changing
      if (referral && referral.status !== newStatus) {
        // Non-blocking call - UI updates immediately, API call happens in background
        onStatusChange(draggedItem, newStatus);
      }
      
      // Clear drag state immediately - don't wait for API
      setDraggedItem(null);
    }
  }, [draggedItem, referrals, onStatusChange]);

  const handleDragEnd = useCallback(() => {
    setDraggedItem(null);
    setDragOverColumn(null);
  }, []);

  // Memoize referrals by status to prevent unnecessary re-renders
  const referralsByStatus = useMemo(() => {
    const grouped: Record<string, ClientReferralWithUI[]> = {};
    referrals.forEach(referral => {
      if (!grouped[referral.status]) {
        grouped[referral.status] = [];
      }
      grouped[referral.status].push(referral);
    });
    return grouped;
  }, [referrals]);
  
  const getReferralsByStatus = useCallback((status: string) => {
    return referralsByStatus[status] || [];
  }, [referralsByStatus]);

  // Dynamic grid columns based on the number of status columns
  const getGridCols = (numColumns: number) => {
    switch (numColumns) {
      case 1: return 'lg:grid-cols-1';
      case 2: return 'lg:grid-cols-2';
      case 3: return 'lg:grid-cols-3';
      case 4: return 'lg:grid-cols-4';
      case 5: return 'lg:grid-cols-5';
      case 6: return 'lg:grid-cols-6';
      case 7: return 'lg:grid-cols-7';
      case 8: return 'lg:grid-cols-8';
      default: return 'lg:grid-cols-6'; // fallback
    }
  };

  return (
    <div
      className={`grid grid-cols-1 md:grid-cols-2 ${getGridCols(statusColumns.length)} gap-4 lg:gap-6 xl:gap-8`}
      style={{ minHeight: '600px' }}
    >
      {statusColumns.map((column) => {
        const columnReferrals = getReferralsByStatus(column.id);
        const isDragOver = dragOverColumn === column.id;
        
        return (
          <div
            key={column.id}
            className={`flex flex-col bg-white rounded-lg border border-gray-200 transition-all duration-200 ${
              isDragOver 
                ? 'border-blue-400 bg-blue-50 shadow-lg scale-[1.02]' 
                : 'hover:border-gray-300 hover:shadow-sm'
            }`}
            onDragOver={(e) => handleDragOver(e, column.id)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, column.id)}
            data-testid={`kanban-column-${column.id}`}
          >
            {/* Column Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <div className={`h-5 w-5 ${column.iconColor}`}>
                  {column.icon}
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 space-grotesk text-sm lg:text-base">
                    {column.title}
                  </h3>
                </div>
              </div>
              <Badge 
                variant="secondary" 
                className={`${column.color} text-xs font-medium space-grotesk`}
              >
                {columnReferrals.length}
              </Badge>
            </div>

            {/* Column Content */}
            <div className="flex-1 p-4 space-y-3 overflow-y-auto">
              {columnReferrals.length === 0 ? (
                <div className="flex items-center justify-center h-32 text-gray-400">
                  <div className="text-center">
                    <div className={`h-8 w-8 mx-auto mb-2 ${column.iconColor} opacity-50`}>
                      {column.icon}
                    </div>
                    <p className="text-sm space-grotesk">No referrals</p>
                  </div>
                </div>
              ) : (
                columnReferrals.map((referral) => (
                  <ClientReferralKanbanCardItem
                    key={referral.id}
                    referral={referral}
                    onViewReferral={onViewReferral}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                    isDragging={draggedItem === referral.id}
                    isSyncing={syncingReferrals.has(referral.id)}
                  />
                ))
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
